import type {
  CockpitArtifactAccess,
  CockpitArtifactRole,
  CockpitConfidence,
  CockpitCoverage,
  CockpitGuaranteeResult,
  CockpitLifecycle,
  CockpitProvenance,
  CockpitRelation,
  CockpitSectionStatus,
  CockpitUsageObservationState,
  SessionEvidenceState,
  SessionIntegrationState,
  SessionObservabilityActionCode,
  SessionObservabilityCapability,
  SessionObservabilityReasonCode,
  UsageExclusionReason,
  UsageReason,
} from '@internal/spassu-code-contract';

export type CockpitNodeKind = 'skill' | 'artifact' | 'control' | 'timeline';

export interface CockpitViewNode {
  id: string;
  kind: CockpitNodeKind;
  label: string;
  confidence: CockpitConfidence;
  lifecycle: CockpitLifecycle | 'available' | 'deleted';
  coverage: CockpitCoverage;
  critical?: boolean;
}

export interface CockpitSkillView extends CockpitViewNode {
  kind: 'skill';
  version: string | null;
  sourceLabel: string;
  attempt: number;
  durationMs: number | null;
  evidenceIds: string[];
  artifactIds: string[];
  limitations: string[];
}

export interface CockpitArtifactView extends CockpitViewNode {
  kind: 'artifact';
  artifactType: string | null;
  phase: string | null;
  path: string | null;
  skillName: string | null;
  role: CockpitArtifactRole;
  access: CockpitArtifactAccess;
  version: string | null;
  revisionKey: string;
  provenance: CockpitProvenance;
  createdAt: string | null;
  invocationId: string | null;
  evidenceIds: string[];
  limitations: string[];
  content?: string | null;
}

export interface CockpitSectionViewState {
  status: CockpitSectionStatus;
  coverage: CockpitCoverage;
  limitations: string[];
}

export interface CockpitHeaderView {
  title: string;
  viaLabel: string;
  statusLabel: string;
  evidenceLabel: string;
  updatedAt: string;
  historical: boolean;
  revision: number | null;
}

export type CockpitRailState =
  | 'evidence'
  | 'running'
  | 'detected'
  | 'inferred'
  | 'covered'
  | 'wait'
  | 'void';

export interface CockpitRailSegment {
  id: string;
  nodeId: string | null;
  label: string;
  state: CockpitRailState;
  durationMs: number;
  startedAt: string;
  endedAt: string;
}

export interface CockpitMetricView {
  id: string;
  label: string;
  rawValue: string | number | null;
  displayValue: string;
  unit: string | null;
  status: CockpitSectionStatus;
  coverage: CockpitCoverage;
  provenance: CockpitProvenance[];
  nature: 'reported' | 'calculated' | null;
  explanation: string;
  unavailableReason: string | null;
  relation: string | null;
  technicalValue: string | null;
  primary: boolean;
  updatedAt: string | null;
  sourceCutoffAt: string;
  periodStartedAt: string | null;
  periodEndedAt: string | null;
  provisional: boolean;
  proportion: string | null;
  reasonCode:
    | UsageReason
    | 'time-window-unavailable'
    | 'section-load-failed'
    | null;
  impact: string | null;
  action: string | null;
  counts: {
    observed: number;
    reconciled: number;
    pending: number;
    excluded: number;
    exclusionReasons: UsageExclusionReason[];
  } | null;
  stale: boolean;
  usageObservationState: CockpitUsageObservationState | null;
}

export interface CockpitControlView extends CockpitViewNode {
  kind: 'control';
  result: CockpitGuaranteeResult;
  resultLabel: string;
  sourceLabel: string;
  required: boolean;
  blocking: boolean;
  evidenceIds: string[];
  observedTotal: number;
  failedTotal: number;
  rationale: string | null;
  expectedAction: string | null;
}

export interface CockpitLimitationView {
  code: string;
  summary: string;
}

export interface CockpitGuaranteeView {
  status: 'complete' | 'incomplete' | 'unavailable';
  statusLabel: string;
  sectionStatus: CockpitSectionStatus;
  coverage: CockpitCoverage;
  reason: string;
  impact: string;
  controls: CockpitControlView[];
  criticalGapIds: string[];
  limitations: CockpitLimitationView[];
}

export interface CockpitSessionCapabilityView {
  capability: SessionObservabilityCapability;
  label: string;
  state: SessionEvidenceState;
  stateLabel: string;
  reasonCode: SessionObservabilityReasonCode | null;
  observedAt: string | null;
}

export interface CockpitSessionJointDiagnosticView {
  summary: string;
  impact: string;
  action: string;
}

export interface CockpitSessionObservabilityView {
  runtimeLabel: string;
  state: SessionIntegrationState;
  stateLabel: string;
  actionCode: SessionObservabilityActionCode;
  actionLabel: string;
  observedAt: string;
  stale: boolean;
  capabilities: CockpitSessionCapabilityView[];
  jointDiagnostic: CockpitSessionJointDiagnosticView | null;
}

export interface CockpitViewModel {
  activity: { id: string; title: string; status: string };
  header: CockpitHeaderView;
  revision: number | null;
  generatedAt: string;
  sessionObservability: CockpitSessionObservabilityView | null;
  guarantee: CockpitGuaranteeView;
  rail: CockpitRailSegment[];
  skills: CockpitSkillView[];
  artifacts: CockpitArtifactView[];
  metrics: CockpitMetricView[];
  relations: CockpitRelation[];
  sectionState: {
    relations: CockpitSectionViewState;
    artifacts: CockpitSectionViewState;
  };
}

export function allCockpitNodes(model: CockpitViewModel): CockpitViewNode[] {
  return [...model.skills, ...model.artifacts, ...model.guarantee.controls];
}
